/**
 * Messaging Component - With personalized AI icebreakers and proper deduplication
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  sendMessage,
  loadMessages,
  subscribeToMessages,
  subscribeToConversations,
  markConversationAsRead,
  deleteConversation,
  blockUser,
  isBlocked,
  getConversationId,
  searchUsernames,
  hasExistingMessages
} from '../services/messagingService';
import { getUsernameFromWallet, getUserProfileByWallet, getWalletFromUsername } from '../userMappingService';
import { formatAddress, generateTemporaryWalletFromUsername, getUsernameFromTemporaryAddress } from '../cryptoOperations';
import { trackMessageSent } from '../services/gamificationService';
import { ARC_TESTNET_NETWORK } from '../config/blockchain';
import Tooltip from './Tooltip';

// Split a message into plain text and tx-hash / URL link segments.
const renderMessageText = (text) => {
  if (!text || typeof text !== 'string') return text;
  // Match 0x + 64 hex chars (tx hash) or URLs.
  const pattern = /(0x[a-fA-F0-9]{64})|((?:https?:\/\/)[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (match[1]) {
      const href = `${ARC_TESTNET_NETWORK.blockExplorer}/tx/${token}`;
      parts.push(
        <a
          key={`tx-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {token}
        </a>
      );
    } else {
      parts.push(
        <a
          key={`url-${key++}`}
          href={token}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {token}
        </a>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const Messaging = ({ 
  walletAddress, 
  onClose, 
  initialRecipient = null, 
  navigate = null, 
  initialProfile = null, 
  initialRecipientWallet = null,
  isMobile = false,
  onOpenTransferModal = null
}) => {
  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const [newMessageAddress, setNewMessageAddress] = useState('');
  const [showDeleteConversationDialog, setShowDeleteConversationDialog] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  
  // Username suggestions state
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  
  // Search suggestions state
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  
  // AI Conversation starters
  const [conversationStarters, setConversationStarters] = useState([]);
  const [showStarters, setShowStarters] = useState(false);
  const [isLoadingStarters, setIsLoadingStarters] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const unsubscribeMessagesRef = useRef(null);
  const unsubscribeConversationsRef = useRef(null);
  const processedRecipientRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const userProfilesRef = useRef({});

  // Keep ref in sync with state
  useEffect(() => {
    userProfilesRef.current = userProfiles;
  }, [userProfiles]);

  // Normalize address
  const normalize = (addr) => addr?.toLowerCase()?.startsWith('0x') 
    ? addr.toLowerCase() 
    : addr ? '0x' + addr.toLowerCase() : null;

  const myAddress = normalize(walletAddress);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  // Show notification
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Load user profile - get username only, never show wallet address
  const loadUserProfile = useCallback(async (address) => {
    const normalizedAddr = normalize(address);
    if (!normalizedAddr) return null;
    if (userProfilesRef.current[normalizedAddr]) return userProfilesRef.current[normalizedAddr];

    try {
      // Check for temporary address first
      const tempUsername = getUsernameFromTemporaryAddress(address);
      if (tempUsername) {
        const profile = { 
          address: normalizedAddr, 
          username: tempUsername, 
          displayName: tempUsername 
        };
        setUserProfiles(prev => ({ ...prev, [normalizedAddr]: profile }));
        return profile;
      }

      // Try to get username from wallet
      const username = await getUsernameFromWallet(normalizedAddr);
      
      // Try to get full profile
      let fullProfile = null;
      try {
        fullProfile = await getUserProfileByWallet(normalizedAddr);
      } catch (e) {
        // No profile available
      }

      // ONLY use username - never show wallet address
      const displayName = fullProfile?.name || fullProfile?.displayName || username || null;

      const profile = {
        address: normalizedAddr,
        username: username || null,
        displayName: displayName || username,
        avatar: fullProfile?.avatarPreview || fullProfile?.avatar || null,
        fullProfile,
        // Extract useful data for AI starters
        tagsList: fullProfile?.tagsList || [],
        bio: fullProfile?.bio || '',
        city: fullProfile?.city || '',
        socialLinks: fullProfile?.socialLinks || []
      };

      setUserProfiles(prev => ({ ...prev, [normalizedAddr]: profile }));
      return profile;
    } catch (error) {
      console.warn('Error loading profile:', error);
      return null;
    }
  }, []);

  // Generate personalized AI conversation starters
  const generateAIStarters = useCallback((targetProfile) => {
    const starters = [];
    
    if (!targetProfile) {
      // Generic starters
      return [
        "Hi! I'd love to connect and learn more about you.",
        "Hey! What brings you to PeopleTag?",
        "Hello! I'm curious about your interests.",
        "Hi there! What are you passionate about?",
        "Hey! What's something exciting you're working on?"
      ];
    }
    
    const targetName = targetProfile?.displayName || targetProfile?.username || '';
    const targetTags = targetProfile?.tagsList || targetProfile?.fullProfile?.tagsList || [];
    const targetBio = targetProfile?.bio || targetProfile?.fullProfile?.bio || '';
    const targetCity = targetProfile?.city || targetProfile?.fullProfile?.city || '';
    const socialLinks = targetProfile?.socialLinks || targetProfile?.fullProfile?.socialLinks || [];
    
    // Tag-based starters (personalized)
    if (Array.isArray(targetTags)) {
      targetTags.slice(0, 3).forEach(tag => {
        const cleanTag = typeof tag === 'string' ? tag.replace(/^#+/, '').trim() : '';
        if (cleanTag) {
          starters.push(`I see you're into ${cleanTag}! What got you interested in that?`);
          starters.push(`Hey! I noticed ${cleanTag} in your profile. Tell me more!`);
          starters.push(`What's your favorite thing about ${cleanTag}?`);
        }
      });
    }
    
    // Name-based starters
    if (targetName && targetName !== targetProfile?.username) {
      starters.push(`Hi ${targetName}! I'd love to learn more about you.`);
      starters.push(`Hey ${targetName}! What's your story?`);
    }
    
    // City-based starters
    if (targetCity) {
      starters.push(`I see you're from ${targetCity}! What's it like there?`);
      starters.push(`What's the best thing about ${targetCity}?`);
    }
    
    // Bio-based starters
    if (targetBio && targetBio.length > 10) {
      starters.push(`Your bio caught my attention! Can you tell me more?`);
    }
    
    // Social-based starters
    if (Array.isArray(socialLinks) && socialLinks.length > 0) {
      const hasGithub = socialLinks.some(s => s?.platform?.toLowerCase() === 'github');
      const hasTwitter = socialLinks.some(s => s?.platform?.toLowerCase() === 'twitter');
      if (hasGithub) {
        starters.push(`I see you're on GitHub! What projects are you working on?`);
      }
      if (hasTwitter) {
        starters.push(`What do you usually tweet about?`);
      }
    }
    
    // Generic fallbacks
    const genericStarters = [
      "What's something you're working on that excites you?",
      "I'd love to hear about your interests and experiences!",
      "What's a topic you could talk about for hours?",
      "What's something unique about your journey?",
      "I'm curious about your perspective on things!"
    ];
    
    starters.push(...genericStarters);
    
    // Shuffle and return top 5
    const shuffled = starters.sort(() => 0.5 - Math.random());
    
    // Prioritize personalized ones (first part of array before generics)
    const personalizedCount = starters.length - genericStarters.length;
    if (personalizedCount > 2) {
      const personalized = shuffled.slice(0, personalizedCount);
      const generic = shuffled.slice(personalizedCount);
      return [...personalized.slice(0, 4), ...generic.slice(0, 1)].slice(0, 5);
    }
    
    return shuffled.slice(0, 5);
  }, []);

  // Search usernames for autocomplete
  const handleSearchUsernames = useCallback(async (term, forSearch = false) => {
    if (!term || term.trim().length < 2) {
      if (forSearch) {
        setSearchSuggestions([]);
        setShowSearchSuggestions(false);
      } else {
        setUsernameSuggestions([]);
        setShowSuggestions(false);
      }
      return;
    }

    // Don't search if it looks like a wallet address
    if (term.startsWith('0x')) {
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const suggestions = await searchUsernames(term);
      if (forSearch) {
        setSearchSuggestions(suggestions);
        setShowSearchSuggestions(suggestions.length > 0);
      } else {
        setUsernameSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      }
    } catch (error) {
      console.error('Error searching usernames:', error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, []);

  // Debounced username search for new message dialog
  useEffect(() => {
    if (showNewMessageDialog && newMessageAddress) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        handleSearchUsernames(newMessageAddress, false);
      }, 300);
      return () => clearTimeout(searchTimeoutRef.current);
    } else {
      setUsernameSuggestions([]);
      setShowSuggestions(false);
    }
  }, [newMessageAddress, showNewMessageDialog, handleSearchUsernames]);

  // Debounced username search for search field
  useEffect(() => {
    if (searchQuery && searchQuery.trim().length >= 2) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        handleSearchUsernames(searchQuery, true);
      }, 300);
      return () => clearTimeout(searchTimeoutRef.current);
    } else {
      setSearchSuggestions([]);
      setShowSearchSuggestions(false);
    }
  }, [searchQuery, handleSearchUsernames]);

  // Subscribe to conversations
  useEffect(() => {
    if (!myAddress) return;

    setIsLoading(true);

    unsubscribeConversationsRef.current = subscribeToConversations(myAddress, async (convs) => {
      // Deduplicate by otherParty address (more reliable than conversationId)
      const uniqueConvs = [];
      const seenOtherParties = new Set();
      for (const conv of convs) {
        const otherPartyLower = conv.otherParty?.toLowerCase();
        if (otherPartyLower && !seenOtherParties.has(otherPartyLower)) {
          seenOtherParties.add(otherPartyLower);
          uniqueConvs.push(conv);
        }
      }
      setConversations(uniqueConvs);
      setIsLoading(false);

      // Load profiles for all conversation participants
      for (const conv of uniqueConvs) {
        loadUserProfile(conv.otherParty);
      }
    });

    return () => {
      unsubscribeConversationsRef.current?.();
    };
  }, [myAddress, loadUserProfile]);

  // Reset processed ref when recipients are cleared (user navigates to /messages without username)
  useEffect(() => {
    if (!initialRecipientWallet && !initialRecipient) {
      processedRecipientRef.current = null;
    }
  }, [initialRecipient, initialRecipientWallet]);

  // Handle initial recipient - WAIT for conversations to load first
  useEffect(() => {
    if (!myAddress) return;
    if (!initialRecipientWallet && !initialRecipient) return;
    // CRITICAL: Wait for conversations to load before processing initial recipient
    // Otherwise we won't find existing conversations and will show icebreakers incorrectly
    if (isLoading) return;
    if (processedRecipientRef.current === (initialRecipientWallet || initialRecipient)) return;

    const setupInitialConversation = async () => {
      processedRecipientRef.current = initialRecipientWallet || initialRecipient;

      try {
        let otherAddress = initialRecipientWallet;
        
        if (!otherAddress && initialRecipient) {
          if (initialRecipient.startsWith('0x')) {
            otherAddress = initialRecipient;
          } else {
            otherAddress = await getWalletFromUsername(initialRecipient);
            if (!otherAddress) {
              otherAddress = generateTemporaryWalletFromUsername(initialRecipient);
            }
          }
        }

        if (!otherAddress) return;

        const normalizedOther = normalize(otherAddress);
        const conversationId = getConversationId(myAddress, normalizedOther);

        const profile = await loadUserProfile(normalizedOther);
        
        // CRITICAL: If initialRecipient is a displayName (not username), correct the URL
        // Get the actual username from the profile
        const actualUsername = profile?.username;
        if (actualUsername && initialRecipient && initialRecipient !== actualUsername && navigate) {
          // The URL has a displayName, but we have the username - correct it!
          console.log('[Messaging] Correcting URL from displayName to username:', {
            old: initialRecipient,
            new: actualUsername
          });
          navigate(`/messages/${encodeURIComponent(actualUsername)}`, { replace: true });
        }

        // Check if this conversation already exists in the list (now that conversations are loaded)
        const existing = conversations.find(c => 
          c.conversationId === conversationId || 
          c.otherParty?.toLowerCase() === normalizedOther
        );

        console.log('[Messaging] Initial recipient setup:', {
          otherAddress: normalizedOther,
          existingInList: !!existing,
          conversationsCount: conversations.length,
          actualUsername
        });

        if (existing) {
          // Conversation exists - mark as from list to prevent icebreakers
          setSelectedConversation({ ...existing, isFromList: true, hasMessages: true });
        } else {
          // Not in list - check Firestore directly to be sure
          const firestoreHasMessages = await hasExistingMessages(myAddress, normalizedOther);
          console.log('[Messaging] Firestore check for initial recipient:', { firestoreHasMessages });
          
          setSelectedConversation({
            conversationId,
            otherParty: normalizedOther,
            unreadCount: 0,
            hasMessages: firestoreHasMessages,
            isFromList: firestoreHasMessages // If Firestore has messages, treat as existing
          });
        }
      } catch (error) {
        console.error('Error setting up initial conversation:', error);
      }
    };

    setupInitialConversation();
  }, [myAddress, initialRecipient, initialRecipientWallet, loadUserProfile, isLoading, conversations, navigate]);

  // Load messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation?.otherParty || !myAddress) return;

    // CRITICAL: Capture all values SYNCHRONOUSLY at the start of the effect
    const otherAddress = selectedConversation.otherParty;
    
    // Also check if this conversation exists in the conversations list
    // This is the MOST RELIABLE check - if it's in the list, it EXISTS
    const conversationInList = conversations.find(c => 
      c.otherParty?.toLowerCase() === otherAddress?.toLowerCase()
    );
    
    const isFromList = selectedConversation.isFromList;
    // Check for lastMessagePreview in selectedConversation FIRST (most reliable)
    const lastMessagePreview = selectedConversation.lastMessagePreview || conversationInList?.lastMessagePreview;
    const hasMessagesFlag = selectedConversation.hasMessages || conversationInList?.hasMessages;
    const unreadCount = selectedConversation.unreadCount || conversationInList?.unreadCount || 0;
    
    // CRITICAL: If ANY of these are true, conversation EXISTS - never show starters
    // Priority order: isFromList > conversationInList > lastMessagePreview > hasMessagesFlag > unreadCount
    const isExistingConversation = isFromList || 
                                    !!conversationInList || 
                                    !!lastMessagePreview || 
                                    hasMessagesFlag || 
                                    unreadCount > 0;
    
    console.log('[Messaging] Effect starting - conversation state:', {
      otherParty: otherAddress,
      isExistingConversation,
      isFromList,
      conversationInList: !!conversationInList,
      lastMessagePreview,
      hasMessagesFlag,
      unreadCount
    });
    
    let isCancelled = false;
    
    setIsLoadingMessages(true);
    setMessages([]);
    processedMessageIds.current.clear();
    setShowStarters(false);
    setConversationStarters([]);

    unsubscribeMessagesRef.current?.();

    const setupMessages = async () => {
      try {
        if (isCancelled) return;
        
        const blocked = await isBlocked(myAddress, otherAddress);
        if (isCancelled) return;
        
        if (blocked) {
          showNotification('This user is blocked', 'error');
          setIsLoadingMessages(false);
          return;
        }

        console.log('[Messaging] Loading messages, isExistingConversation:', isExistingConversation);

        // Load existing messages
        const msgs = await loadMessages(myAddress, otherAddress, 50);
        if (isCancelled) return;
        
        console.log('[Messaging] Loaded messages:', msgs.length);
        
        msgs.forEach(m => processedMessageIds.current.add(m.messageId));
        setMessages(msgs);
        setIsLoadingMessages(false);
        scrollToBottom();

        // CRITICAL RULE: Only show starters for TRULY NEW conversations
        // NEVER show starters if:
        // 1. Conversation was clicked from list (isFromList = true) - HIGHEST PRIORITY
        // 2. Conversation exists in conversations list
        // 3. Conversation has lastMessagePreview
        // 4. Conversation has hasMessages flag
        // 5. Conversation has unreadCount > 0
        // 6. Messages were loaded (msgs.length > 0)
        
        // If clicked from list, NEVER show starters - period
        if (isFromList) {
          console.log('[Messaging] Conversation from list - hiding starters');
          setShowStarters(false);
          setConversationStarters([]);
        } else if (conversationInList || lastMessagePreview || hasMessagesFlag || unreadCount > 0 || msgs.length > 0) {
          // Any indicator of existing conversation - hide starters
          console.log('[Messaging] Existing conversation indicators found - hiding starters', {
            conversationInList: !!conversationInList,
            lastMessagePreview,
            hasMessagesFlag,
            unreadCount,
            msgsCount: msgs.length
          });
          setShowStarters(false);
          setConversationStarters([]);
        } else {
          // Might be new - double-check Firestore to be absolutely sure
          console.log('[Messaging] No indicators found - checking Firestore');
          const firestoreHasMessages = await hasExistingMessages(myAddress, otherAddress);
          if (isCancelled) return;
          
          console.log('[Messaging] Firestore check result:', { firestoreHasMessages });
          
          if (!firestoreHasMessages) {
            // Truly new conversation - show starters
            setIsLoadingStarters(true);
            const targetProfile = await loadUserProfile(otherAddress);
            if (isCancelled) return;
            
            const starters = generateAIStarters(targetProfile);
            setConversationStarters(starters);
            setShowStarters(true);
            setIsLoadingStarters(false);
          } else {
            // Firestore has messages - hide starters
            setShowStarters(false);
            setConversationStarters([]);
          }
        }

        await markConversationAsRead(myAddress, otherAddress);

        // Subscribe to new messages
        unsubscribeMessagesRef.current = subscribeToMessages(
          myAddress,
          otherAddress,
          (newMsg) => {
            if (isCancelled) return;
            if (!processedMessageIds.current.has(newMsg.messageId)) {
              processedMessageIds.current.add(newMsg.messageId);
              setMessages(prev => {
                // Double-check for duplicates
                if (prev.some(m => m.messageId === newMsg.messageId)) return prev;
                const updated = [...prev, newMsg].sort((a, b) => 
                  a.timestamp.getTime() - b.timestamp.getTime()
                );
                return updated;
              });
              scrollToBottom();
              setShowStarters(false);

              if (!newMsg.isMine) {
                markConversationAsRead(myAddress, otherAddress);
              }
            }
          }
        );
      } catch (error) {
        if (isCancelled) return;
        console.error('Error loading messages:', error);
        showNotification('Failed to load messages', 'error');
        setIsLoadingMessages(false);
      }
    };

    setupMessages();

    return () => {
      isCancelled = true; // Mark this effect as cancelled
      unsubscribeMessagesRef.current?.();
    };
  }, [selectedConversation, conversations, myAddress, scrollToBottom, showNotification, loadUserProfile, generateAIStarters]);

  // Send message
  const handleSendMessage = async (textOverride = null) => {
    const textToSend = textOverride || messageInput.trim();
    if (!textToSend || !selectedConversation?.otherParty || isSending) return;

    if (!textOverride) setMessageInput('');
    setIsSending(true);
    setShowStarters(false);

    try {
      const sentMsg = await sendMessage(myAddress, selectedConversation.otherParty, textToSend);
      
      // Add message locally only if not already added
      if (!processedMessageIds.current.has(sentMsg.messageId)) {
        processedMessageIds.current.add(sentMsg.messageId);
        setMessages(prev => {
          if (prev.some(m => m.messageId === sentMsg.messageId)) return prev;
          return [...prev, sentMsg];
        });
        scrollToBottom();
      }

      try {
        await trackMessageSent(myAddress);
      } catch (e) {
        // Non-critical
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Failed to send message: ' + error.message, 'error');
      if (!textOverride) setMessageInput(textToSend);
    } finally {
      setIsSending(false);
    }
  };

  // Start new conversation from username
  const handleStartConversation = async (recipientUsername) => {
    const recipient = recipientUsername || newMessageAddress.trim();
    if (!recipient) return;

    if (recipient.startsWith('0x')) {
      showNotification('Please enter a username, not a wallet address', 'error');
      return;
    }

    try {
      const otherAddress = await getWalletFromUsername(recipient);
      if (!otherAddress) {
        showNotification(`User "${recipient}" not found`, 'error');
        return;
      }

      const normalizedOther = normalize(otherAddress);
      const conversationId = getConversationId(myAddress, normalizedOther);

      const profile = await loadUserProfile(normalizedOther);
      
      // CRITICAL: Get the actual username from the profile, not the input
      // The input might be a displayName, but URL must always use username
      const actualUsername = profile?.username || recipient;

      // Check if this conversation already exists in the list
      const existing = conversations.find(c => 
        c.conversationId === conversationId || 
        c.otherParty?.toLowerCase() === normalizedOther
      );

      if (existing) {
        setSelectedConversation({ ...existing, isFromList: true });
      } else {
        setSelectedConversation({
          conversationId,
          otherParty: normalizedOther,
          unreadCount: 0,
          hasMessages: false,
          isFromList: false
        });
      }

      setShowNewMessageDialog(false);
      setNewMessageAddress('');
      setShowSuggestions(false);
      setUsernameSuggestions([]);
      setSearchQuery('');
      setShowSearchSuggestions(false);

      // CRITICAL: Always use username in URL, never displayName or input value
      if (navigate && actualUsername) {
        navigate(`/messages/${encodeURIComponent(actualUsername)}`);
      } else if (navigate) {
        console.warn('Cannot navigate: username not available for', normalizedOther);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      showNotification('Failed to start conversation: ' + error.message, 'error');
    }
  };

  // Handle search suggestion click - open existing or new conversation
  const handleSearchSuggestionClick = async (suggestion) => {
    const username = suggestion.username;
    setSearchQuery('');
    setShowSearchSuggestions(false);
    
    try {
      const otherAddress = await getWalletFromUsername(username);
      if (!otherAddress) return;
      
      const normalizedOther = normalize(otherAddress);
      const conversationId = getConversationId(myAddress, normalizedOther);
      
      // Check if conversation exists in the list
      const existing = conversations.find(c => 
        c.conversationId === conversationId || 
        c.otherParty?.toLowerCase() === normalizedOther
      );
      
      await loadUserProfile(normalizedOther);
      
      if (existing) {
        // Existing conversation - mark as from list so starters don't show
        setSelectedConversation({ ...existing, isFromList: true });
      } else {
        // New conversation - starters can show
        setSelectedConversation({
          conversationId,
          otherParty: normalizedOther,
          unreadCount: 0,
          hasMessages: false,
          isFromList: false
        });
      }
      
      if (navigate) {
        navigate(`/messages/${encodeURIComponent(username)}`);
      }
    } catch (error) {
      console.error('Error opening conversation:', error);
    }
  };

  // Block user
  const handleBlockUser = async () => {
    if (!selectedConversation?.otherParty) return;

    try {
      await blockUser(myAddress, selectedConversation.otherParty);
      showNotification('User blocked', 'success');
      setShowBlockDialog(false);
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      showNotification('Failed to block user', 'error');
    }
  };

  // Delete conversation
  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    setIsDeleting(true);
    try {
      await deleteConversation(myAddress, conversationToDelete.otherParty);
      
      setConversations(prev => prev.filter(c => c.conversationId !== conversationToDelete.conversationId));
      
      if (selectedConversation?.conversationId === conversationToDelete.conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }

      showNotification('Conversation deleted', 'success');
    } catch (error) {
      showNotification('Failed to delete conversation', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteConversationDialog(false);
      setConversationToDelete(null);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Get display name (username only, never wallet address)
  const getDisplayName = useCallback((address) => {
    const profile = userProfiles[address];
    if (profile?.displayName) return profile.displayName;
    if (profile?.username) return profile.username;
    
    const tempUsername = getUsernameFromTemporaryAddress(address);
    if (tempUsername) return tempUsername;
    
    return null;
  }, [userProfiles]);

  // Get other party profile
  const otherPartyProfile = useMemo(() => {
    if (!selectedConversation?.otherParty) return null;
    return userProfiles[selectedConversation.otherParty] || null;
  }, [selectedConversation?.otherParty, userProfiles]);

  // Filter conversations
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter(conv => {
      const profile = userProfiles[conv.otherParty];
      const displayName = profile?.displayName || profile?.username || conv.displayName;
      return displayName?.toLowerCase().includes(query);
    });
  }, [conversations, searchQuery, userProfiles]);

  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: isMobile ? 0 : '50%',
    right: 0,
    bottom: 0,
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 10000,
    borderLeft: isMobile ? 'none' : '1px solid #e0e0e0',
    boxShadow: isMobile ? 'none' : '-2px 0 8px rgba(0,0,0,0.1)',
    minWidth: 0,
    overflow: 'hidden'
  };

  return (
    <div style={containerStyle} className={isMobile ? 'mobile-panel' : ''}>
      {/* Header */}
      <div style={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Messages</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => setShowHelp(!showHelp)}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              border: '1px solid #ccc',
              backgroundColor: showHelp ? '#f0f0f0' : '#fff',
              color: '#666',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
            title="Help"
          >
            ?
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: notification.type === 'error' ? '#e74c3c' : notification.type === 'success' ? '#27ae60' : '#3498db',
          color: '#fff',
          padding: '10px 20px',
          borderRadius: '8px',
          zIndex: 10001,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          {notification.message}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Conversation List */}
        <div style={{
          width: '300px',
          backgroundColor: '#fff',
          borderRight: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Search with suggestions */}
          <div style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchSuggestions.length > 0 && setShowSearchSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            
            {/* Search Suggestions Dropdown */}
            {showSearchSuggestions && searchSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '12px',
                right: '12px',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {searchSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSearchSuggestionClick(suggestion)}
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      borderBottom: idx < searchSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#e0e0e0',
                      backgroundImage: suggestion.avatar ? `url(${suggestion.avatar})` : 'none',
                      backgroundSize: 'cover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#666'
                    }}>
                      {!suggestion.avatar && suggestion.username?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 500 }}>@{suggestion.username}</div>
                      {suggestion.name && suggestion.name !== suggestion.username && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{suggestion.name}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Conversations */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* New Conversation Button */}
            <div
              onClick={() => setShowNewMessageDialog(true)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
            >
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#e0e0e0',
                border: '2px dashed #999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '20px', color: '#666' }}>+</span>
              </div>
              <span style={{ color: '#666' }}>New conversation</span>
            </div>

            {isLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Loading conversations...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                {searchQuery ? 'No matching conversations' : 'No conversations yet'}
              </div>
            ) : (
              filteredConversations.map(conv => {
                const displayName = getDisplayName(conv.otherParty) || conv.displayName;
                const profile = userProfiles[conv.otherParty];
                const isSelected = selectedConversation?.conversationId === conv.conversationId;

                if (!displayName) return null;

                return (
                  <div
                    key={conv.conversationId}
                    onClick={async () => {
                      // Mark as from list - this conversation EXISTS, never show starters
                      // If it's in the list, it definitely has messages or metadata
                      // IMMEDIATELY hide starters - don't wait for messages to load
                      setShowStarters(false);
                      setConversationStarters([]);
                      setSelectedConversation({ 
                        ...conv, 
                        isFromList: true,
                        hasMessages: conv.hasMessages || !!conv.lastMessagePreview || conv.unreadCount > 0
                      });
                      setShowHelp(false);
                      
                      // CRITICAL: Always use username in URL, never displayName
                      // Get username from profile, or load it if not available
                      let username = profile?.username;
                      if (!username) {
                        // Profile might not be loaded yet - load it to get username
                        const loadedProfile = await loadUserProfile(conv.otherParty);
                        username = loadedProfile?.username;
                      }
                      
                      // Fallback: if still no username, try to get it from wallet
                      if (!username) {
                        try {
                          username = await getUsernameFromWallet(conv.otherParty);
                        } catch (e) {
                          console.warn('Could not get username for navigation:', e);
                        }
                      }
                      
                      // Only navigate if we have a username (never use displayName in URL)
                      if (navigate && username) {
                        navigate(`/messages/${encodeURIComponent(username)}`);
                      } else if (navigate) {
                        console.warn('Cannot navigate: username not available for', conv.otherParty);
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e8f4fd' : '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => !isSelected && (e.currentTarget.style.backgroundColor = '#f9f9f9')}
                    onMouseLeave={(e) => !isSelected && (e.currentTarget.style.backgroundColor = '#fff')}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#e0e0e0',
                      backgroundImage: profile?.avatar ? `url(${profile.avatar})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#666',
                      flexShrink: 0
                    }}>
                      {!profile?.avatar && displayName.charAt(0).toUpperCase()}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px'
                      }}>
                        <span style={{
                          fontWeight: conv.unreadCount > 0 ? 600 : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {displayName}
                        </span>
                        {conv.unreadCount > 0 && (
                          <span style={{
                            backgroundColor: '#007bff',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            flexShrink: 0
                          }}>
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                      {/* Message preview or time */}
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#666', 
                        marginTop: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '180px'
                      }}>
                        {conv.lastMessagePreview ? (
                          <span>
                            {conv.lastMessageSender === myAddress ? 'You: ' : ''}
                            {conv.lastMessagePreview}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>
                            Start conversation...
                          </span>
                        )}
                      </div>
                      {conv.lastMessageTime && (
                        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                          {formatTime(conv.lastMessageTime)}
                        </div>
                      )}
                    </div>

                    {/* Delete button - Red X */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConversationToDelete(conv);
                        setShowDeleteConversationDialog(true);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#dc3545',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        opacity: 0.6,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                      title="Delete conversation"
                    >
                      ✕
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          overflow: 'hidden'
        }}>
          {showHelp ? (
            /* Help Content - Expanded */
            <div style={{ 
              flex: 1, 
              padding: '20px', 
              overflowY: 'auto',
              backgroundColor: '#f9f9f9',
              maxWidth: '800px',
              margin: '0 auto',
              width: '100%'
            }}>
              {/* Header */}
              <div style={{
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '2px solid #e0e0e0'
              }}>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '24px', 
                  fontWeight: 700, 
                  color: '#1a1a1a',
                  marginBottom: '4px'
                }}>
                  Messages Help Center
                </h2>
                <p style={{ 
                  margin: 0, 
                  fontSize: '14px', 
                  color: '#666',
                  lineHeight: 1.4
                }}>
                  Learn how to use PeopleTag messaging securely and effectively
                </p>
              </div>

              {/* Section: End-to-End Encryption */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#e8f4fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0
                  }}>
                    🔒
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#1a1a1a'
                  }}>
                    End-to-End Encryption
                  </h3>
                </div>
                <p style={{ 
                  lineHeight: 1.5, 
                  color: '#555',
                  fontSize: '14px',
                  marginBottom: '12px',
                  marginLeft: '50px'
                }}>
                  All messages are encrypted using <strong style={{ color: '#007bff' }}>AES-256-GCM</strong> with keys derived from 
                  wallet addresses via <strong style={{ color: '#007bff' }}>ECDH (Elliptic Curve Diffie-Hellman)</strong> key exchange. 
                  This ensures:
                </p>
                <ul style={{ 
                  color: '#555', 
                  lineHeight: 1.6,
                  marginLeft: '50px',
                  paddingLeft: '18px',
                  fontSize: '14px',
                  marginTop: 0,
                  marginBottom: 0
                }}>
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Encrypted at source:</strong> Messages are encrypted <em>before</em> leaving your device
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Private conversations:</strong> Only you and the recipient can read the messages
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    <strong>Server-proof:</strong> Even PeopleTag servers cannot decrypt your conversations
                  </li>
                  <li style={{ marginBottom: '0' }}>
                    <strong>Unique keys:</strong> Each conversation has its own encryption key
                  </li>
                </ul>
              </div>
              
              {/* Section: How Messaging Works */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#e8f5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0
                  }}>
                    💬
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#1a1a1a'
                  }}>
                    How Messaging Works
                  </h3>
                </div>
                <ol style={{ 
                  lineHeight: 1.6, 
                  color: '#555',
                  marginLeft: '50px',
                  paddingLeft: '20px',
                  fontSize: '14px',
                  counterReset: 'step-counter',
                  listStyle: 'none',
                  marginTop: 0,
                  marginBottom: 0
                }}>
                  <li style={{ 
                    marginBottom: '10px',
                    paddingLeft: '28px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      top: '1px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#007bff',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>1</span>
                    <strong style={{ color: '#1a1a1a' }}>Start a conversation:</strong> Click "New conversation" or search for a username
                  </li>
                  <li style={{ 
                    marginBottom: '10px',
                    paddingLeft: '28px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      top: '1px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#007bff',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>2</span>
                    <strong style={{ color: '#1a1a1a' }}>AI Icebreakers:</strong> When starting a new chat, personalized conversation starters are generated based on the user's profile, tags, and interests
                  </li>
                  <li style={{ 
                    marginBottom: '10px',
                    paddingLeft: '28px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      top: '1px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#007bff',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>3</span>
                    <strong style={{ color: '#1a1a1a' }}>Real-time messaging:</strong> Messages appear instantly for both users
                  </li>
                  <li style={{ 
                    marginBottom: '0',
                    paddingLeft: '28px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      top: '1px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#007bff',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>4</span>
                    <strong style={{ color: '#1a1a1a' }}>Message status:</strong> ✓ = sent
                  </li>
                </ol>
              </div>

              {/* Section: Finding Users */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#fff4e6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0
                  }}>
                    🔍
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#1a1a1a'
                  }}>
                    Finding Users
                  </h3>
                </div>
                <ul style={{ 
                  lineHeight: 1.6, 
                  color: '#555',
                  marginLeft: '50px',
                  paddingLeft: '18px',
                  fontSize: '14px',
                  marginTop: 0,
                  marginBottom: 0
                }}>
                  <li style={{ marginBottom: '6px' }}>
                    Type a username in the search bar to find existing or new conversations
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    Suggestions appear as you type (minimum 2 characters)
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    Click a suggestion to open/start conversation
                  </li>
                  <li style={{ marginBottom: '0' }}>
                    <strong>Privacy first:</strong> Only usernames are shown - wallet addresses are never displayed
                  </li>
                </ul>
              </div>

              {/* Section: TagAI Conversation Starters */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#ffe8e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0
                  }}>
                    🎯
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#1a1a1a'
                  }}>
                    TagAI Conversation Starters
                  </h3>
                </div>
                <p style={{ 
                  lineHeight: 1.5, 
                  color: '#555',
                  fontSize: '14px',
                  marginBottom: '12px',
                  marginLeft: '50px'
                }}>
                  When you start a new conversation, <strong style={{ color: '#007bff' }}>TagAI</strong> generates personalized icebreakers 
                  based on:
                </p>
                <ul style={{ 
                  color: '#555', 
                  lineHeight: 1.6,
                  marginLeft: '50px',
                  paddingLeft: '18px',
                  fontSize: '14px',
                  marginTop: 0,
                  marginBottom: 0
                }}>
                  <li style={{ marginBottom: '6px' }}>
                    The user's interest tags (e.g., "I see you're into music!")
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    Their published bio and location
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    Connected social media profiles
                  </li>
                  <li style={{ marginBottom: '0' }}>
                    Shared interests between you and them
                  </li>
                </ul>
              </div>

              {/* Section: Privacy Controls */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#f3e8ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0
                  }}>
                    ⚙️
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#1a1a1a'
                  }}>
                    Privacy Controls
                  </h3>
                </div>
                <ul style={{ 
                  lineHeight: 1.6, 
                  color: '#555',
                  marginLeft: '50px',
                  paddingLeft: '18px',
                  fontSize: '14px',
                  marginTop: 0,
                  marginBottom: 0
                }}>
                  <li style={{ marginBottom: '6px' }}>
                    <strong style={{ color: '#1a1a1a' }}>Block users:</strong> Click "Block" to prevent messages from a user
                  </li>
                  <li style={{ marginBottom: '6px' }}>
                    <strong style={{ color: '#1a1a1a' }}>Delete conversations:</strong> Click ✕ next to a conversation
                  </li>
                  <li style={{ marginBottom: '0' }}>
                    <strong style={{ color: '#1a1a1a' }}>Encrypted storage:</strong> Messages are stored encrypted in Firestore (not IPFS)
                  </li>
                </ul>
              </div>

              {/* Section: Technical Details */}
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '16px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0
                  }}>
                    🚀
                  </div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#1a1a1a'
                  }}>
                    Technical Details
                  </h3>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '12px',
                  marginLeft: '50px'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Encryption</div>
                    <div style={{ fontSize: '13px', color: '#333' }}>AES-256-GCM with random 12-byte nonce</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Key Derivation</div>
                    <div style={{ fontSize: '13px', color: '#333' }}>ECDH using wallet's public key</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Storage</div>
                    <div style={{ fontSize: '13px', color: '#333' }}>Google Cloud Firestore (encrypted at rest)</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>Real-time</div>
                    <div style={{ fontSize: '13px', color: '#333' }}>Firestore onSnapshot subscriptions</div>
                  </div>
                </div>
              </div>
            </div>
          ) : selectedConversation ? (
            <>
              {/* Chat Header */}
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#e0e0e0',
                    backgroundImage: otherPartyProfile?.avatar ? `url(${otherPartyProfile.avatar})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#666'
                  }}>
                    {!otherPartyProfile?.avatar && (getDisplayName(selectedConversation.otherParty) || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div 
                      style={{ fontSize: '16px', fontWeight: 600, cursor: 'pointer', color: '#007bff' }}
                      onClick={() => {
                        const username = getDisplayName(selectedConversation.otherParty);
                        if (navigate && username) {
                          navigate(`/profile/${encodeURIComponent(username)}`);
                        }
                      }}
                    >
                      {getDisplayName(selectedConversation.otherParty) || 'Unknown User'}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {onOpenTransferModal && (
                    <button
                      onClick={() => {
                        const username = getDisplayName(selectedConversation.otherParty);
                        onOpenTransferModal({
                          username: username && !username.startsWith('0x') ? username : null,
                          walletAddress: selectedConversation.otherParty,
                        });
                      }}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: '#4a90e2',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    >
                      Send USDC
                    </button>
                  )}
                  <Tooltip text="Messages are end-to-end encrypted">
                    <span style={{ fontSize: '18px', cursor: 'help' }}>🔒</span>
                  </Tooltip>
                  <button
                    onClick={() => setShowBlockDialog(true)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #dc3545',
                      borderRadius: '6px',
                      backgroundColor: '#fff',
                      color: '#dc3545',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Block
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div
                ref={messagesContainerRef}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '16px',
                  backgroundColor: '#f9f9f9'
                }}
              >
                {(isLoadingMessages || (selectedConversation?.isFromList && selectedConversation?.lastMessagePreview && messages.length === 0)) ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#666'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
                    <div>Loading messages...</div>
                  </div>
                ) : messages.length === 0 && !selectedConversation?.isFromList ? (
                  <div style={{ textAlign: 'center', color: '#666', marginTop: '40px' }}>
                    <p>No messages yet. Start the conversation!</p>
                    <p style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>
                      Messages are automatically encrypted 🔒
                    </p>
                    
                    {/* AI Conversation Starters */}
                    {showStarters && conversationStarters.length > 0 && !isLoadingMessages && !selectedConversation?.isFromList && (
                      <div style={{ marginTop: '24px', textAlign: 'left', maxWidth: '500px', margin: '24px auto' }}>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px', textAlign: 'center' }}>
                          <strong>💡 TagAI Conversation Starters:</strong>
                        </p>
                        {conversationStarters.map((starter, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleSendMessage(starter)}
                            style={{
                              padding: '12px 16px',
                              backgroundColor: '#fff',
                              border: '2px solid #e0e0e0',
                              borderRadius: '12px',
                              marginBottom: '10px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: '#333',
                              transition: 'all 0.2s',
                              borderLeft: '4px solid #007bff'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e8f4fd';
                              e.currentTarget.style.borderColor = '#007bff';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#fff';
                              e.currentTarget.style.borderColor = '#e0e0e0';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                          >
                            {starter}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {isLoadingStarters && (
                      <div style={{ marginTop: '20px', color: '#999' }}>
                        <span>🤖 Generating personalized starters...</span>
                      </div>
                    )}
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isOwn = msg.isMine || msg.sender?.toLowerCase() === myAddress;
                    return (
                      <div
                        key={msg.messageId || index}
                        style={{
                          display: 'flex',
                          justifyContent: isOwn ? 'flex-end' : 'flex-start',
                          marginBottom: '12px'
                        }}
                      >
                        <div style={{
                          maxWidth: '70%',
                          padding: '10px 14px',
                          borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          backgroundColor: isOwn ? '#007bff' : '#fff',
                          color: isOwn ? '#fff' : '#333',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {renderMessageText(msg.text)}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            marginTop: '4px',
                            opacity: 0.7,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: isOwn ? 'flex-end' : 'flex-start'
                          }}>
                            {formatTime(msg.timestamp)}
                            {isOwn && (
                              <span>✓</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={{
                padding: '12px 16px',
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                gap: '12px',
                backgroundColor: '#fff'
              }}>
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  disabled={isSending}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '24px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!messageInput.trim() || isSending}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '24px',
                    backgroundColor: messageInput.trim() && !isSending ? '#007bff' : '#ccc',
                    color: '#fff',
                    cursor: messageInput.trim() && !isSending ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  {isSending ? '...' : 'Send'}
                </button>
              </div>
            </>
          ) : (
            /* No conversation selected */
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
              <div style={{ fontSize: '18px', fontWeight: 500 }}>Select a conversation</div>
              <div style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
                or start a new one
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Message Dialog */}
      {showNewMessageDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>New Conversation</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Enter a username to start chatting:
            </p>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="@username"
                value={newMessageAddress}
                onChange={(e) => setNewMessageAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleStartConversation()}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() => usernameSuggestions.length > 0 && setShowSuggestions(true)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                autoFocus
              />
              
              {/* Username Suggestions */}
              {showSuggestions && usernameSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 10002,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  {usernameSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleStartConversation(suggestion.username)}
                      onMouseDown={(e) => e.preventDefault()}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        borderBottom: index < usernameSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#e0e0e0',
                        backgroundImage: suggestion.avatar ? `url(${suggestion.avatar})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#666'
                      }}>
                        {!suggestion.avatar && (suggestion.username?.charAt(0)?.toUpperCase() || '?')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                          @{suggestion.username}
                        </div>
                        {suggestion.name && suggestion.name !== suggestion.username && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {suggestion.name}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowNewMessageDialog(false);
                  setNewMessageAddress('');
                  setShowSuggestions(false);
                  setUsernameSuggestions([]);
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleStartConversation()}
                disabled={!newMessageAddress.trim()}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: newMessageAddress.trim() ? '#007bff' : '#ccc',
                  color: '#fff',
                  cursor: newMessageAddress.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Start Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Dialog */}
      {showBlockDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>Block User</h3>
            <p>Are you sure you want to block this user?</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setShowBlockDialog(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBlockUser}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Conversation Dialog */}
      {showDeleteConversationDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>Delete Conversation</h3>
            <p>Are you sure you want to delete this conversation?</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowDeleteConversationDialog(false);
                  setConversationToDelete(null);
                }}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConversation}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: isDeleting ? '#ccc' : '#dc3545',
                  color: '#fff',
                  cursor: isDeleting ? 'not-allowed' : 'pointer'
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messaging;
